import { lazy } from 'react'
import Root from './root/view/Root.tsx'
import { ResetPasswordTokenLoader } from '@/routes/resetPassword/token/loader.ts'
import { LoaderFunctionArgs, Outlet, RouteObject, ShouldRevalidateFunction } from 'react-router-dom'
import { SETTINGS_ROUTES } from '@/routes/settings/routes.tsx'
import { PROJECT_STAGES_ROUTES } from '@/routes/projectStages/routes.tsx'
import { SUPERUSER_ROUTES } from '@/routes/superuser/routes.tsx'
import { HELP_ROUTES } from '@/help/routes.tsx'
import { startRoutes } from '@/routes/start/routes.tsx'
const Error = lazy(() => import('./ErrorPage.tsx'))
import { publicIssueLoader } from '@/routes/publicIssuePreview/actions.ts'
const Login = lazy(() => import('@/routes/login/Login.tsx'))
const ResetPassword = lazy(() => import('@/routes/resetPassword/view/ResetPassword.tsx'))
const ResetPasswordToken = lazy(() => import('@/routes/resetPassword/token/ResetPasswordToken.tsx'))
const SignupForm = lazy(() => import('@/routes/signup/view/SignupForm.tsx'))
const SignupConfirm = lazy(() => import('@/routes/signup/view/SignupConfirm.tsx'))
const GlobalLayout = lazy(() => import('@/routes/GlobalLayout.tsx'))
const ProjectSingleIssue = lazy(
  () => import('@/routes/projectStages/issue/view/ProjectSingleIssue.tsx'),
)
const ProtectedRoute = lazy(() => import('@/components/common/wrappers/ProtectedRoute.tsx'))
const NotFound = lazy(() => import('@/routes/NotFound.tsx'))
const PublicReport = lazy(() => import('@/routes/publicReport/PublicReportPage.tsx'))
import SuspenseWrapper from '@/components/common/wrappers/SuspenseWrapper.tsx'
import { rootLoader } from '@/routes/root/lib/actions/rootLoader.ts'
import loaderCommon from '@/lib/helpers/loaderCommon.ts'

const routes: RouteObject[] = [
  {
    element: <GlobalLayout />,
    errorElement: <Error />,
    children: [
      {
        path: 'not-found',
        element: <NotFound />,
      },
      {
        path: 'login',
        element: (
          <SuspenseWrapper>
            <Login />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'reset_password',
        element: (
          <SuspenseWrapper>
            <Outlet />
          </SuspenseWrapper>
        ),
        children: [
          {
            index: true,
            element: <ResetPassword />,
          },
          {
            path: ':token',
            element: <ResetPasswordToken />,
            loader: ResetPasswordTokenLoader,
          },
        ],
      },
      {
        // Public signup from a campaign link (docs/plans/rejestracja-z-linku.md, B4). Outside
        // ProtectedRoute like reset_password: the visitor has no account yet. The static
        // `confirm` segment outranks `:code`, and no link code can be `confirm` — codes are
        // 16 Crockford base32 symbols (SignupLinkService::generateCode).
        path: 'signup',
        element: (
          <SuspenseWrapper>
            <Outlet />
          </SuspenseWrapper>
        ),
        children: [
          { path: 'confirm', element: <SignupConfirm /> },
          { path: ':code', element: <SignupForm /> },
        ],
      },
      {
        id: 'publicIssue',
        path: 'public/issues/:token',
        loader: publicIssueLoader,
        // Own boundary. This route mounts the same `ProjectSingleIssue` subtree
        // as the authenticated stage route, so it carries the same commit-phase
        // crash surface — and react-dom hands such failures to the NEAREST
        // boundary. Without one here that is the root `errorElement` below
        // `GlobalLayout`, so a single failed insertion took the whole shell
        // down. Keeping `GlobalLayout` mounted is safe for anonymous visitors:
        // it renders no authenticated chrome, only the wrapper, the maintenance
        // overlay and the toaster.
        errorElement: <Error />,
        element: (
          <SuspenseWrapper>
            <ProjectSingleIssue />
          </SuspenseWrapper>
        ),
      },
      {
        id: 'publicReport',
        path: 'public/reports/:token',
        element: (
          <SuspenseWrapper>
            <PublicReport />
          </SuspenseWrapper>
        ),
      },
      {
        id: 'publicReportIssue',
        path: 'public/reports/:token/issues/:issueId',
        element: (
          <SuspenseWrapper>
            <PublicReport />
          </SuspenseWrapper>
        ),
      },
      ...startRoutes(),
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <Root />
          </ProtectedRoute>
        ),
        loader: (params: LoaderFunctionArgs) => loaderCommon(rootLoader, params),
        shouldRevalidate: (({ currentParams, nextParams }) => {
          // only revalidate if the project stage ID has changed
          const shouldRevalidate = currentParams?.projectStageId !== nextParams?.projectStageId
          // console.log({ shouldRevalidate })

          return shouldRevalidate
        }) as ShouldRevalidateFunction,

        children: [PROJECT_STAGES_ROUTES, SETTINGS_ROUTES, SUPERUSER_ROUTES, HELP_ROUTES],
      },
    ],
  },
]

export default routes
